import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { Grid } from '@material-ui/core';

import {
    ViewTemplateIcon,
    ReplaceTemplateIcon,
    EditTemplateIcon,
    Typography,
    SimpleToggle
} from '@dev/ee-components';
import { FormSettingsEnum } from '@dev/ee-api/ee-api-models';
import { includes, map } from 'lodash-es';

import { getApiPath } from '@/services/Secuirty';
import { ShowDialogParams } from '@/store/dialog/types';
import { ShowDialog } from '@/store/dialog/actions';
import { SetIsTemplateOpen, SetTemplatePreview, TemplateDeselect, getFormCreatorStore } from '@/store/formCreator/actions';

import { Divider } from '@/assets/Divider';
import { TemplatePreviewButton } from '@/assets/TemplatePreviewButton';
import { ScreenshotWrapper, ScreenshotHover, TitleWrapper, AudienceSection } from './styles';

type Step2ContentProps = {
    isEditMode: boolean;
}

export const Step2Content = ({isEditMode}:Step2ContentProps): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { formObject } = useSelector(getFormCreatorStore);
    const isMailingPreference = includes(formObject?.FormSettings, FormSettingsEnum.MailingPreference);
    const [mailingPreferences, setMailingPreferences] = useState(isMailingPreference);
    const templateScreenshot = formObject?.Screenshot ?? formObject?.MediumScreenshot;

    useEffect(() => { setMailingPreferences(isMailingPreference)}, [isMailingPreference] )

    const editTemplate = (): void => {
        dispatch(SetIsTemplateOpen(true));
    };

    const viewTemplate = (): void => {
        dispatch(SetTemplatePreview(formObject?.Html));
    };
    
    const deselectTemplate = (): void => {
        dispatch(TemplateDeselect());
    };

    const replaceTemplate = (): void => {
        const dialogParams: ShowDialogParams = {
            title: t('replacetemplate'),
            confirmLabel: t('confirm'),
            body: t('replacetemplatedescription'),
            onClickConfirm: deselectTemplate,
            cancelLabel: t('cancel'),
        };
        dispatch(ShowDialog(dialogParams));
    };

    const mappedAudience = (): JSX.Element[] =>
        map(formObject?.Audience, (audience) => {
            const { ListName } = audience;
            return <Typography variant="body1">{ListName}</Typography>
        });

    return (
        <Grid container xs={12} justifyContent="center" style={{ marginTop: 32 }}>
            <Grid item xs={6}>
                <ScreenshotWrapper>
                    <ScreenshotHover>
                        <Grid item style={{ justifySelf: 'flex-end', marginTop: 24, marginRight: 24 }}>
                            {isEditMode && (
                                <TemplatePreviewButton
                                    id={"forms-replace-template"}
                                    buttonIcon={<ReplaceTemplateIcon width={28.8} height={28.8} />}
                                    buttonLabel={t('replace')}
                                    handleOnClick={replaceTemplate}
                                />
                            )}
                        </Grid>
                        <Grid
                            container
                            style={{ marginTop: 24, marginBottom: 24, gap: 16 }}
                            direction="row"
                            justifyContent="center"
                        >
                            <TemplatePreviewButton
                                id={"form-view-template"}
                                buttonIcon={<ViewTemplateIcon width={28.8} height={28.8} />}
                                buttonLabel={t('view')}
                                handleOnClick={viewTemplate}
                            />
                            {isEditMode && (
                                <TemplatePreviewButton
                                    id={"form-edit-template"}
                                    buttonIcon={<EditTemplateIcon width={31.5} height={31.5} />}
                                    buttonLabel={t('edit')}
                                    handleOnClick={editTemplate}
                                />
                            )}
                        </Grid>
                    </ScreenshotHover>
                    <img src={`${getApiPath()}/userfile${encodeURI(templateScreenshot)}`} />
                </ScreenshotWrapper>
            </Grid>
            <Grid item xs={6} style={{ paddingLeft: 30 }}>
                <Divider />
                <TitleWrapper>
                    <Typography variant="h2">{t('audience')}</Typography>
                </TitleWrapper>
                <SimpleToggle
                    id="forms-mailing-preferences-toggle"
                    value="mailingPreferences"
                    label={t('mailingpreferences_lower')}
                    checked={mailingPreferences}
                    disabled
                />
                <AudienceSection>
                    <Typography variant='body1'>{t('contactswillbeaddedto')}</Typography>
                    {mappedAudience()}
                </AudienceSection>
                <Divider marginTop={32}/>
            </Grid>
        </Grid>
    );
}

